import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Bell,
  Loader2,
  Trash2,
  MessageCircle,
  Users,
  UserPlus,
  Megaphone,
  ShieldAlert,
  KeyRound,
  Coins,
  Phone,
  ShieldCheck,
  Check,
  Monitor,
  Smartphone,
  LogOut,
  MapPin,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { timeAgo } from "@/lib/format";
import { lookupGeo } from "@/lib/geoip";
import { getTrustedCcs, subscribeTrust, trustCc, untrustCc } from "@/lib/sessionTrust";
import { LanguageCard, PrivacyCard } from "@/components/settings/LanguagePrivacyCards";
import { EncryptionDevicesCard } from "@/components/settings/EncryptionDevicesCard";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const PREFS = [
  { key: "dms", label: "Direct messages", desc: "New DMs and replies", icon: MessageCircle },
  { key: "circles", label: "BLOQs", desc: "BLOQ activity and live rooms", icon: Users },
  { key: "connections", label: "Connection requests", desc: "When someone asks to connect", icon: UserPlus },
  { key: "announcements", label: "Announcements", desc: "Product news and beta updates", icon: Megaphone },
];

function NotificationPrefs() {
  const qc = useQueryClient();
  const { data: prefs, isLoading } = useQuery({
    queryKey: ["notification-prefs"],
    queryFn: api.notificationPrefs,
  });
  const [saving, setSaving] = useState(null);

  const mut = useMutation({
    mutationFn: (patch) => api.updateNotificationPrefs(patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ["notification-prefs"] });
      const prev = qc.getQueryData(["notification-prefs"]);
      qc.setQueryData(["notification-prefs"], (o) => ({ ...(o || {}), ...patch }));
      return { prev };
    },
    onError: (_e, _patch, ctx) => {
      qc.setQueryData(["notification-prefs"], ctx?.prev);
      toast.error("Couldn't update preference");
    },
    onSuccess: (data) => qc.setQueryData(["notification-prefs"], data),
    onSettled: () => setSaving(null),
  });

  const toggle = (key, val) => {
    setSaving(key);
    mut.mutate({ [key]: val });
  };

  return (
    <div className="rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="notification-prefs-card">
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
        <Bell className="h-4 w-4 text-bloq-mint" /> Notifications
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8 text-bloq-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="space-y-1">
          {PREFS.map(({ key, label, desc, icon: Icon }) => (
            <div
              key={key}
              className="flex items-center gap-3 rounded-2xl px-1 py-3"
              data-testid={`pref-row-${key}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/5 text-bloq-muted">
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-bloq-muted">{desc}</div>
              </div>
              {saving === key ? (
                <Loader2 className="h-4 w-4 animate-spin text-bloq-muted" />
              ) : (
                <Switch
                  checked={!!prefs?.[key]}
                  onCheckedChange={(v) => toggle(key, v)}
                  data-testid={`pref-toggle-${key}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DM_GATE_KINDS = [
  { key: "native", label: "Native coin" },
  { key: "token", label: "Token (ERC-20)" },
  { key: "nft", label: "NFT" },
];

// Lane A ▸ A3 — token-gated DMs. Only holders of the chosen asset can start a NEW DM; contacts always bypass.
function DmGateCard() {
  const { user, refreshUser } = useAuth();
  const gate = user?.dm_gate;
  const { data: chainsData } = useQuery({ queryKey: ["wallet-chains"], queryFn: api.walletChains });
  const chains = chainsData?.chains || [];
  const [kind, setKind] = useState("token");
  const [chain, setChain] = useState("");
  const [contract, setContract] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [symbol, setSymbol] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const list = chainsData?.chains || [];
    if (!chain && list.length) setChain(list[0].id);
  }, [chainsData, chain]);

  const enable = async () => {
    if (!minAmount || Number(minAmount) <= 0) return toast.error("Enter a minimum balance greater than 0.");
    if (kind !== "native" && !contract.trim()) return toast.error("Enter the token or NFT contract address.");
    setBusy(true);
    try {
      const payload = { chain, kind, min_amount: Number(minAmount) };
      if (kind !== "native") payload.contract = contract.trim();
      if (symbol.trim()) payload.symbol = symbol.trim();
      await api.setDmGate(payload);
      await refreshUser();
      toast.success("Token gate enabled — only holders can start new DMs");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const raw = (typeof d === "string" && d) || d?.message || "";
      const friendly = kind !== "native" && /decimals|erc-?20|erc-?721|erc-?1155|not.*token|invalid.*contract/i.test(raw)
        ? `That doesn't look like a valid ${kind === "nft" ? "NFT" : "ERC-20 token"} contract on this chain.`
        : raw || "Couldn't enable the token gate.";
      toast.error(friendly);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.clearDmGate();
      await refreshUser();
      toast.success("Token gate removed — DMs are contacts-only");
    } catch {
      toast.error("Couldn't remove the token gate.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="dm-gate-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <Coins className="h-4 w-4 text-bloq-purple" /> Token-gated DMs
      </div>
      <p className="text-xs text-bloq-muted">
        Require a token, coin or NFT balance for <span className="text-white/80">strangers</span> to start a new DM. Your contacts always bypass the gate.
      </p>

      {gate ? (
        <div className="mt-4" data-testid="dm-gate-active">
          <div className="flex items-center gap-3 rounded-2xl border border-bloq-purple/25 bg-bloq-purple/[0.06] p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bloq-purple/20 text-bloq-purple">
              <Coins className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold" data-testid="dm-gate-display">{gate.display || `${gate.min_balance ?? ""} ${gate.symbol || gate.kind}`}</div>
              <div className="truncate text-xs capitalize text-bloq-muted">{gate.chain} · {gate.kind}{gate.contract ? ` · ${gate.contract.slice(0, 6)}…${gate.contract.slice(-4)}` : ""}</div>
            </div>
          </div>
          <button onClick={disable} disabled={busy} className="mt-3 inline-flex items-center gap-1 rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted hover:text-destructive disabled:opacity-50" data-testid="dm-gate-remove">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Remove gate
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="dm-gate-form">
          <div className="flex flex-wrap gap-2">
            {DM_GATE_KINDS.map((k) => (
              <button key={k.key} onClick={() => setKind(k.key)} className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${kind === k.key ? "border-bloq-purple/50 bg-bloq-purple/15 text-bloq-purple" : "border-white/10 text-bloq-muted hover:text-white"}`} data-testid={`dm-gate-kind-${k.key}`}>
                {k.label}
              </button>
            ))}
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Chain</Label>
            <select value={chain} onChange={(e) => setChain(e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-white/12 bg-bloq-bg px-3 text-sm text-white focus:border-bloq-mint focus:outline-none" data-testid="dm-gate-chain-select">
              {chains.map((c) => <option key={c.id} value={c.id}>{`${c.label} (${c.symbol})`}</option>)}
            </select>
          </div>
          {kind !== "native" && (
            <div>
              <Label className="text-xs text-bloq-muted">Contract address</Label>
              <Input value={contract} onChange={(e) => setContract(e.target.value)} placeholder="0x…" className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg font-mono" data-testid="dm-gate-contract-input" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-bloq-muted">Minimum balance</Label>
              <Input type="number" min="0" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="e.g. 1" className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="dm-gate-min-input" />
            </div>
            <div>
              <Label className="text-xs text-bloq-muted">Symbol (optional)</Label>
              <Input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="e.g. BLOQ" className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="dm-gate-symbol-input" />
            </div>
          </div>
          <Button onClick={enable} disabled={busy} className="w-full rounded-full bg-bloq-purple font-bold text-white hover:opacity-90 disabled:opacity-50" data-testid="dm-gate-enable">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Enable token gate"}
          </Button>
        </div>
      )}
    </div>
  );
}

function PhoneVerifyCard() {
  const { user, refreshUser } = useAuth();
  const verified = !!user?.phone_verified;
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("enter"); // enter | code
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);

  const errMsg = (e, fallback) => {
    const d = e?.response?.data?.detail;
    return (typeof d === "string" && d) || d?.message || fallback;
  };

  const sendCode = async () => {
    const p = phone.trim().replace(/[\s\-()]/g, "");
    if (!/^\+?[0-9]{7,15}$/.test(p)) return toast.error("Enter a valid number with country code, e.g. +14155550123.");
    setBusy(true);
    try {
      const res = await api.phoneStart(p);
      setHint(res?.phone_hint || "");
      setStage("code");
      toast.success("We texted you a 6-digit code.");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't send a code — check the number and try again."));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    const c = code.trim();
    if (c.length < 4) return toast.error("Enter the code from the text message.");
    setBusy(true);
    try {
      await api.phoneVerify(c);
      await refreshUser();
      toast.success("Phone verified 🎉");
      setCode("");
      setStage("enter");
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 410
          ? "That code expired — tap Resend to get a new one."
          : errMsg(e, "That code didn't match — try again.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="phone-card" className="scroll-mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="phone-verify-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <Phone className="h-4 w-4 text-bloq-mint" /> Phone number
      </div>
      <p className="text-xs text-bloq-muted">
        Verify your phone to boost your trust score and unlock SMS two-factor login. We never show it publicly.
      </p>

      {verified ? (
        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-bloq-mint/25 bg-bloq-mint/[0.06] p-4" data-testid="phone-verified-state">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bloq-mint/15 text-bloq-mint">
            <Check className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white">Phone verified</div>
            <div className="truncate text-xs text-bloq-muted">{user?.phone || "Your number is confirmed"}</div>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="phone-verify-form">
          {stage === "enter" ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-bloq-muted">Phone number</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 415 555 0123"
                  inputMode="tel"
                  className="h-11 rounded-xl border-white/12 bg-bloq-bg"
                  data-testid="phone-input"
                />
              </div>
              <Button onClick={sendCode} disabled={busy} className="rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-50" data-testid="phone-send-code">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-bloq-muted" data-testid="phone-code-hint">
                Enter the 6-digit code we texted{hint ? ` to ${hint}` : ""}.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-bloq-muted">Verification code</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                  placeholder="123456"
                  inputMode="numeric"
                  className="h-11 rounded-xl border-white/12 bg-bloq-bg tracking-[0.3em]"
                  data-testid="phone-code-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={verify} disabled={busy} className="rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-50" data-testid="phone-verify-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                </Button>
                <button onClick={sendCode} disabled={busy} className="text-xs font-semibold text-bloq-muted hover:text-white disabled:opacity-50" data-testid="phone-resend">
                  Resend code
                </button>
                <button onClick={() => { setStage("enter"); setCode(""); }} className="text-xs font-semibold text-bloq-muted hover:text-white" data-testid="phone-change-number">
                  Change number
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TwoFactorCard() {
  const { user, refreshUser } = useAuth();
  const phoneVerified = !!user?.phone_verified;
  const enabled = !!user?.twofa_sms;
  const [busy, setBusy] = useState(false);

  const toggle = async (v) => {
    if (v && !phoneVerified) return toast.error("Verify your phone number first to enable SMS 2FA.");
    setBusy(true);
    try {
      const res = await api.phone2fa(v);
      await refreshUser();
      toast.success(res?.twofa_sms ? "SMS two-factor enabled — you'll get a code at login" : "SMS two-factor disabled");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" && d) || "Couldn't update two-factor settings.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="twofa-card" className="scroll-mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="twofa-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <ShieldCheck className="h-4 w-4 text-bloq-purple" /> Two-factor authentication
        <span className="rounded-full bg-white/8 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-bloq-muted">Optional</span>
      </div>
      <p className="text-xs text-bloq-muted">
        Add a one-time SMS code at login for an extra layer of security. Requires a verified phone number.
      </p>
      <div className="mt-4 flex items-center gap-3 rounded-2xl border border-white/8 px-4 py-3.5" data-testid="twofa-row">
        <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", enabled ? "bg-bloq-mint/15 text-bloq-mint" : "bg-white/5 text-bloq-muted")}>
          <ShieldCheck className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-white">SMS two-factor</div>
          <div className="text-xs text-bloq-muted">
            {enabled ? "On — you'll get a code at login" : phoneVerified ? "Off" : "Verify your phone first to enable"}
          </div>
        </div>
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-bloq-muted" />
        ) : (
          <Switch checked={enabled} disabled={!phoneVerified} onCheckedChange={toggle} data-testid="twofa-toggle" />
        )}
      </div>
    </div>
  );
}

function DeleteAccountCard() {
  const { deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const ready = password.length > 0 && confirmText.trim() === "DELETE";

  const run = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      await deleteAccount(password);
      toast.success("Your account has been deleted");
      navigate("/login");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg =
        (typeof d === "string" && d) ||
        d?.message ||
        e?.response?.data?.message ||
        "Couldn't delete account — check your password and try again.";
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl border border-destructive/25 bg-destructive/[0.05] p-5" data-testid="danger-zone-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-destructive">
        <ShieldAlert className="h-4 w-4" /> Danger zone
      </div>
      <p className="text-xs text-bloq-muted">
        Deleting your account is permanent. Your profile, chats and BLOQ Score are removed. This can't be undone.
      </p>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="mt-4 rounded-full border-destructive/40 bg-transparent font-semibold text-destructive hover:bg-destructive/10"
        data-testid="delete-account-btn"
      >
        <Trash2 className="mr-1.5 h-4 w-4" /> Delete my account
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setPassword(""); setConfirmText(""); } }}>
        <DialogContent className="max-w-md border-destructive/30 bg-bloq-s1 text-white" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Delete your account</DialogTitle>
            <DialogDescription className="text-bloq-muted">
              This is permanent. Confirm your password and type <span className="font-bold text-white">DELETE</span> to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your current password"
                className="h-11 rounded-xl border-white/12 bg-bloq-bg"
                data-testid="delete-account-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Type DELETE to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="h-11 rounded-xl border-white/12 bg-bloq-bg"
                data-testid="delete-account-confirm-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5"
              data-testid="delete-account-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={run}
              disabled={!ready || busy}
              className="rounded-full font-bold"
              data-testid="delete-account-submit"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && confirm !== next;
  const canSubmit = current.length > 0 && next.length >= 8 && confirm === next && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.changePassword(current, next);
      toast.success("Password updated", { description: "You're still signed in on this device." });
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg =
        (typeof d === "string" && d) ||
        (Array.isArray(d) && (d[0]?.msg || "Please check your new password.")) ||
        d?.message ||
        e?.response?.data?.message ||
        "Couldn't change password — please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div id="change-password" className="scroll-mt-4 rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="change-password-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <KeyRound className="h-4 w-4 text-bloq-mint" /> Password
      </div>
      <p className="text-xs text-bloq-muted">
        Change your password. Use at least 8 characters. You'll stay signed in on this device.
      </p>
      <div className="mt-4 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-bloq-muted">Current password</Label>
          <Input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            placeholder="Your current password"
            className="h-11 rounded-xl border-white/12 bg-bloq-bg"
            data-testid="current-password-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-bloq-muted">New password</Label>
          <Input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            placeholder="At least 8 characters"
            className="h-11 rounded-xl border-white/12 bg-bloq-bg"
            data-testid="new-password-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider text-bloq-muted">Confirm new password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            placeholder="Re-type your new password"
            className="h-11 rounded-xl border-white/12 bg-bloq-bg"
            data-testid="confirm-password-input"
          />
          {mismatch && <p className="text-xs text-destructive" data-testid="password-mismatch">Passwords don't match.</p>}
        </div>
        <Button
          onClick={submit}
          disabled={!canSubmit}
          className="rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-50"
          data-testid="change-password-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
        </Button>
      </div>
    </div>
  );
}

function ActiveSessionsCard() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["auth-sessions"], queryFn: api.listSessions });
  const sessions = data?.sessions || [];
  // Newest sign-in first — this doubles as the "recent sign-ins" log.
  const sorted = [...sessions].sort(
    (a, b) => new Date(b.last_active || b.created_at) - new Date(a.last_active || a.created_at),
  );
  const [busy, setBusy] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [geo, setGeo] = useState({});
  const [trusted, setTrusted] = useState(getTrustedCcs());
  useEffect(() => subscribeTrust(() => setTrusted(getTrustedCcs())), []);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["auth-sessions"] });

  const revokeOne = async (sid) => {
    setBusy(sid);
    try { await api.revokeSession(sid); toast.success("Signed out that device"); invalidate(); }
    catch { toast.error("Couldn't sign out that device."); }
    finally { setBusy(null); }
  };
  // Report-suspicious: sign the device out AND nudge the user to change their password.
  const reportOne = async (sid) => {
    setBusy(sid);
    try {
      await api.revokeSession(sid);
      invalidate();
      toast.warning("Signed out that device", {
        description: "If you don't recognize this sign-in, change your password now.",
        duration: 12000,
        action: {
          label: "Change password",
          onClick: () => document.getElementById("change-password")?.scrollIntoView({ behavior: "smooth", block: "start" }),
        },
      });
    } catch { toast.error("Couldn't sign out that device."); }
    finally { setBusy(null); }
  };
  const revokeOthers = async () => {
    setBusy("others");
    try { await api.revokeOtherSessions(); toast.success("Signed out all other devices"); invalidate(); }
    catch { toast.error("Couldn't sign out other devices."); }
    finally { setBusy(null); }
  };

  const others = sessions.filter((s) => !s.current).length;

  useEffect(() => {
    const list = showAll ? sorted : sorted.slice(0, 8);
    [...new Set(list.map((s) => s.ip).filter(Boolean))].forEach(async (ip) => {
      if (geo[ip] !== undefined) return;
      const g = await lookupGeo(ip);
      setGeo((prev) => ({ ...prev, [ip]: g || null }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, showAll]);

  // Country of the current device — the trusted baseline. Any other session from a different
  // country the user hasn't trusted is flagged as a new/unfamiliar location.
  const currentCc = (() => {
    const cur = sessions.find((s) => s.current);
    return cur ? geo[cur.ip]?.cc || null : null;
  })();

  return (
    <div className="rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="active-sessions-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <Monitor className="h-4 w-4 text-bloq-mint" /> Active sessions
      </div>
      <p className="text-xs text-bloq-muted">Your most recent sign-ins, newest first. Sign out or report anything you don't recognize.</p>
      {isLoading ? (
        <div className="flex justify-center py-8 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="sessions-list">
          {(showAll ? sorted : sorted.slice(0, 8)).map((s) => {
            const Icon = ["ios", "android", "mobile"].includes(s.platform) ? Smartphone : Monitor;
            const g = geo[s.ip];
            const newLocation = !s.current && currentCc && g?.cc && g.cc !== currentCc && !trusted.includes(g.cc);
            return (
              <div key={s.id} className={cn("overflow-hidden rounded-2xl border bg-bloq-s2", newLocation ? "border-amber-500/40" : "border-white/8")} data-testid={`session-${s.id}`}>
                <div className="flex items-center gap-3 px-4 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/5 text-bloq-muted"><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <span className="truncate">{s.label || s.platform || "Device"}</span>
                      {s.current && <span className="rounded-full bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-bloq-mint" data-testid="session-current">This device</span>}
                      {newLocation && (
                        <span className="flex items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-400" data-testid={`session-new-location-${s.id}`} title={`Signed in from ${g.label} — a different country than this device`}>
                          <MapPin className="h-2.5 w-2.5" /> New location
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-bloq-muted">
                      <span className="truncate">{g?.label || s.ip || "—"} · active {timeAgo(s.last_active || s.created_at)}</span>
                      {!s.current && !newLocation && (
                        <button onClick={() => reportOne(s.id)} disabled={busy === s.id} className="shrink-0 font-semibold underline decoration-dotted underline-offset-2 transition-colors hover:text-destructive disabled:opacity-40" data-testid={`session-report-${s.id}`}>· Report</button>
                      )}
                    </div>
                  </div>
                  {!s.current && (
                    <button onClick={() => revokeOne(s.id)} disabled={busy === s.id} className="flex h-8 w-8 items-center justify-center rounded-lg text-bloq-muted transition-colors hover:bg-destructive/15 hover:text-destructive disabled:opacity-40" data-testid={`session-revoke-${s.id}`} title="Sign out this device">
                      {busy === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                    </button>
                  )}
                </div>
                {newLocation && (
                  <div className="flex border-t border-amber-500/20">
                    <button onClick={() => reportOne(s.id)} disabled={busy === s.id} className="flex flex-1 items-center justify-center gap-1.5 bg-amber-500/10 py-2 text-[11px] font-semibold text-amber-400 transition-colors hover:bg-amber-500/20 disabled:opacity-40" data-testid={`session-nudge-revoke-${s.id}`}>
                      {busy === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><LogOut className="h-3.5 w-3.5" /> Not you? Sign out</>}
                    </button>
                    <button onClick={() => { trustCc(g.cc); toast.success(`Trusted sign-ins from ${g.cc}`); }} className="flex flex-1 items-center justify-center gap-1.5 border-l border-amber-500/20 py-2 text-[11px] font-semibold text-bloq-muted transition-colors hover:bg-white/5 hover:text-white" data-testid={`session-trust-${s.id}`} title={`Stop flagging sign-ins from ${g.cc}`}>
                      <ShieldCheck className="h-3.5 w-3.5" /> This was me
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {sessions.length === 0 && <p className="py-4 text-center text-xs text-bloq-muted">No active sessions found.</p>}
          {sessions.length > 8 && (
            <button onClick={() => setShowAll((v) => !v)} className="w-full pt-1 text-center text-xs font-semibold text-bloq-mint hover:underline" data-testid="sessions-show-more">
              {showAll ? "Show fewer" : `Show all ${sessions.length} sessions`}
            </button>
          )}
          {others > 0 && (
            <Button variant="outline" onClick={revokeOthers} disabled={busy === "others"} className="mt-2 w-full rounded-full border-destructive/40 bg-transparent font-semibold text-destructive hover:bg-destructive/10" data-testid="sessions-revoke-others">
              {busy === "others" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="mr-1.5 h-4 w-4" /> Sign out all other devices</>}
            </Button>
          )}
          {trusted.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-white/8 pt-3" data-testid="trusted-locations">
              <span className="inline-flex items-center gap-1 text-[11px] text-bloq-muted"><ShieldCheck className="h-3 w-3 text-bloq-mint" /> Trusted locations:</span>
              {trusted.map((cc) => (
                <button key={cc} onClick={() => { untrustCc(cc); toast.success(`Stopped trusting ${cc}`); }} className="inline-flex items-center gap-1 rounded-full border border-bloq-mint/25 bg-bloq-mint/10 px-2 py-0.5 text-[10px] font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/20" data-testid={`trusted-cc-${cc}`} title="Stop trusting this location">
                  {cc} <X className="h-2.5 w-2.5" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Deep-link from the activation checklist: ?focus=phone|2fa scrolls to that card.
  useEffect(() => {
    const focus = searchParams.get("focus");
    const id = focus === "phone" ? "phone-card" : focus === "2fa" ? "twofa-card" : null;
    if (id) {
      const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="settings-page">
      <div className="mx-auto max-w-2xl px-5 py-6 sm:px-8">
        <div className="mb-5 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full p-1 text-bloq-muted hover:text-white"
            data-testid="settings-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Settings</h1>
            <p className="mt-0.5 text-sm text-bloq-muted">Notifications & account</p>
          </div>
        </div>

        <div className="space-y-4">
          <NotificationPrefs />
          <LanguageCard />
          <PrivacyCard />
          <EncryptionDevicesCard />
          <PhoneVerifyCard />
          <TwoFactorCard />
          <DmGateCard />
          <ActiveSessionsCard />
          <ChangePasswordCard />
          <DeleteAccountCard />
        </div>
      </div>
    </div>
  );
}
